import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';

import { Loader } from '@googlemaps/js-api-loader';

import RoomIcon       from '@mui/icons-material/Room';
import Grid           from '@mui/material/Grid';
import TextField      from '@mui/material/TextField';
import List           from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon   from '@mui/material/ListItemIcon';
import ListItemText   from '@mui/material/ListItemText';
import Typography     from '@mui/material/Typography';
import grey           from '@mui/material/colors/grey';

import isEmpty    from 'lodash/isEmpty';

import debounce   from 'helpers/debounce';

import OutsideAlerter from '../Generic/OutsideAlerter';

/**
 * @param requiredPlaceTypes Required types to exist to consider an address valid. Supported types -> `https://developers.google.com/maps/documentation/places/web-service/supported_types`
 * */
function Autocomplete({
  apiKey, name, locale, value = '', onPlaceSelect, inputRef, disabled, placeholder = '', requiredPlaceTypes = [], variant="outlined"
}) {
  // This ref is just for PlacesService. It's not working without it.
  const divRef = useRef(null);

  const [error, setError]         = useState('');
  const [query, setQuery]         = useState(value);
  const [places, setPlaces]       = useState([]);
  const [suggests, setSuggests]   = useState(false);

  const [autoCompleteService, setAutoCompleteService] = useState();
  const [sessionToken, setSessionToken]               = useState();

  const placeTypes  = ['geocode', 'establishment'];
  const restriction = { country: [] };
  const placeData   = ['address_components', 'formatted_address', 'geometry', 'place_id']; // https://developers.google.com/places/web-service/place-data-fields

  useEffect(() => {
    setQuery(value);
  }, [value]);

  function initializeService() {
    if (!window.google) throw new Error('[react-google-places-autocomplete]: Google script not loaded');
    if (!window.google.maps) throw new Error('[react-google-places-autocomplete]: Google maps script not loaded');
    if (!window.google.maps.places) throw new Error('[react-google-places-autocomplete]: Google maps places script not loaded');

    setAutoCompleteService(new window.google.maps.places.AutocompleteService());
  }

  useEffect(() => {
    const init = async () => {
      try {
        const key = apiKey || (window.EXTRA_DATA || {}).PLACES_API_KEY;
        const options = {
          apiKey: key, language: locale, libraries: ['places'],
        };

        await new Loader(options).load();
        initializeService();
      } catch (error) { console.log('google-load-script-error', error); }
    };
    init();
  }, []);

  function getSession() {
    if (sessionToken) return sessionToken;

    // https://developers.google.com/places/web-service/session-tokens
    const token = new window.google.maps.places.AutocompleteSessionToken();
    setSessionToken(token);
    return token;
  }

  function resetSession() {
    // Reset session after a full search cycle to avoid penalty.
    setSessionToken(null);
  }

  function setSuggestions(suggestions) {
    setPlaces(suggestions);

    if (isEmpty(suggestions)) {
      onPlaceSelect(null);
      setError(t('eos.landing_page.text.invalid_location'));
      return;
    }

    setSuggests(true);
    setError('');
  }

  function fetchSuggestions(input) {
    setQuery(input);

    if (input.length === 0) {
      setError('');
      onPlaceSelect(null);
      return;
    };

    if (input.length < 3) return;

    debounce(async () => {
      const request = {
        input:                 input,
        componentRestrictions: restriction,
        sessionToken:          getSession(),
        types:                 placeTypes,
      };

      autoCompleteService.getPlacePredictions(request, setSuggestions);
    }, 1000, 'fetch-suggestions');
  }

  function parseSelected(results, status) {
    if (status !== window.google.maps.places.PlacesServiceStatus.OK) return;

    // NOTE: we'll generate our own formatted address to match exactly what we save vs what we display
    let formatted = { street: [], locality: null, administrative_area: [], country: null };
    let location  = { street: [], locality: null, administrative_area: null, country: null, postal_code: null, latitude: null, longitude: null };

    // Extract and map data from address object
    results.address_components.forEach((component) => {
      const type = component.types[0];

      if (type.match(/administrative_area_level/)) {
        if (location.administrative_area !== null) return;

        location.administrative_area = component['long_name'];
        formatted.administrative_area.push(location.administrative_area);

      } else if (type == 'country') {
        location.country  = component['short_name'];
        formatted.country = component['long_name'];

      } else if (type == 'postal_code') {
        location.postal_code = component['short_name'];
        formatted.administrative_area.unshift(location.postal_code);

      } else if (type == 'locality') {
        location.locality  = component['long_name'];
        formatted.locality = location.locality;

      } else {
        const name = Object.keys(results.address_components).length >= 5 ? component['short_name'] : component['long_name'];
        location.street.push(name);
      }
    });

    let isComplete = true;
    if (requiredPlaceTypes.length > 0) {
      for(const type of requiredPlaceTypes) {
        if(location[type]) continue;

        isComplete = false;
        break;
      }
    }

    if (!isComplete) {
      onPlaceSelect(null);
      setError(t('eos.landing_page.text.invalid_location'));
      return;
    }

    location.street    = location.street.join(', ');
    location.latitude  = results.geometry.location.lat();
    location.longitude = results.geometry.location.lng();

    formatted.street              = location.street;
    formatted.administrative_area = formatted.administrative_area.join(' ');

    const formatted_address = Object.values(formatted).filter(n => n).join(', ');

    location.payload = {
      place_id: results.place_id
    };

    location.formatted_address = formatted_address;

    setQuery(formatted_address);
    onPlaceSelect(location);
  }

  function fetchDetails(selected, event) {
    event.preventDefault();

    const placeService = new window.google.maps.places.PlacesService(divRef.current);

    const request = {
      placeId:      selected.place_id,
      fields:       placeData,
      sessionToken: getSession(),
    };

    placeService.getDetails(request, parseSelected);
    setSuggests(false);
  }

  function Suggestions({ suggestions }) {
    if (!suggests || isEmpty(suggestions)) return null;

    const list = suggestions.map((item) => {
      // https://talkpush.atlassian.net/browse/TP-7715
      // we are restricting continents manually. please see ticket above
      if (item.types && item.types.includes('continent')) return null;

      return (
        <ListItemButton key={item.structured_formatting.main_text} sx={{ px: 2, py: 0, minHeight: '56px' }} onClick={(e) => fetchDetails(item, e)}>
          <ListItemIcon sx={{
            minWidth: 'auto', backgroundColor: '#c4cce0', width: '38px', height: '38px', borderRadius: '2px', display: 'flex', justifyContent: 'center', mr: 1,
          }}
          >
            <RoomIcon fontSize="large" sx={{ color: 'white' }} />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {item.structured_formatting.main_text}
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
              {item.structured_formatting.secondary_text}
            </Typography>
          </ListItemText>
        </ListItemButton>
      );
    });

    return (
      <Grid container sx={{ mt: '3px', position: 'relative', width: '100%' }}>
        <Grid
          container
          item
          md={12}
          sx={{
            position: 'absolute', border: 1, borderRadius: 1, borderColor: grey[400], backgroundColor: 'white', zIndex: 1,
          }}
        >
          <List sx={{ width: '100%' }} component="nav">
            {list}
          </List>
          <Grid
            item
            md={12}
            sx={{
              display: 'flex', justifyContent: 'end', px: 2, py: 1, width: '100%',
            }}
          >
            <img alt="Google" src="/img/logo/powered-by-google.png" width="100" />
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <OutsideAlerter onAlert={() => { setSuggests(false); resetSession(); }} style={{ minWidth: '100%' }}>
      <span ref={divRef} />
      <TextField
        error={error.length > 0}
        id={name}
        ref={inputRef}
        type="text"
        name={name}
        onChange={(e) => fetchSuggestions(e.target.value)}
        value={query}
        autoComplete="Off"
        placeholder={placeholder}
        sx={{ width: '100%' }}
        helperText={error}
        variant={variant}
        disabled={disabled}
        data-testid="autocomplete-input"
      />
      <Suggestions suggestions={places} />
    </OutsideAlerter>
  );
}

Autocomplete.propTypes = {
  apiKey:        PropTypes.string,
  name:          PropTypes.string,
  locale:        PropTypes.string,
  value:         PropTypes.string,
  onPlaceSelect: PropTypes.func,
  inputRef:      PropTypes.func,
  placeholder:   PropTypes.string,
  disabled:      PropTypes.bool
};

export default Autocomplete;
